import { createEmojiPicker } from './emoji-picker';

export function messageActions(currentUserId) {
  return {
    // ─────────────────────────────────────────
    // Message Loading
    // ─────────────────────────────────────────
    async loadMessages(conversationId, loadMore = false, { aroundMessageId = null, loadNewer = false } = {}) {
      if (this.activeConversationId !== conversationId) return;

      const pageSize = 50;
      if (loadNewer && (this.activeConversation.messagesOffset || 0) <= 0) return;

      this.activeConversation.isLoading = true;

      try {
        const query = { limit: pageSize };
        if (!loadMore && !loadNewer && aroundMessageId) {
          query.around_message_id = aroundMessageId;
        } else if (loadNewer) {
          const newerOffset = this.activeConversation.messagesOffset || 0;
          query.offset = Math.max(0, newerOffset - pageSize);
          query.limit = Math.min(pageSize, newerOffset);
        } else if (loadMore) {
          // DESC offset of the oldest edge of the current window
          query.offset = (this.activeConversation.messagesOffset || 0)
            + this.activeConversation.messages.length;
        } else {
          query.offset = 0;
        }

        const response = await this.$api({
          url: `/chat/conversations/${conversationId}/messages`,
          method: 'GET',
          query
        });

        const newMessages = response.data.messages;
        const responseOffset = response.data.offset ?? query.offset ?? 0;

        if (loadMore) {
          this.activeConversation.messages = this.mergeMessagesById(
            newMessages,
            this.activeConversation.messages
          );
          this.activeConversation.hasMore = !!response.data.has_more;
        } else if (loadNewer) {
          this.activeConversation.messages = this.mergeMessagesById(
            this.activeConversation.messages,
            newMessages
          );
          this.activeConversation.messagesOffset = responseOffset;
          this.activeConversation.hasNewer = !!response.data.has_newer;
        } else {
          this.activeConversation.messages = newMessages;
          this.activeConversation.messagesOffset = responseOffset;
          this.activeConversation.hasMore = !!response.data.has_more;
          this.activeConversation.hasNewer = !!response.data.has_newer;
          // Store initial read status from API response (only on initial load)
          if (response.data.other_participant_last_read_at) {
            this.activeConversation.otherParticipantLastReadAt =
              response.data.other_participant_last_read_at;
          }

          const focusMessageId = aroundMessageId || this.activeConversation.focusMessageId;
          if (focusMessageId) {
            this.highlightAndScrollToMessage(focusMessageId);
          } else {
            this.scrollToBottom();
          }

          // Mark conversation as read
          this.markConversationAsRead(conversationId);
        }

        // Clear unread count since we just read the messages
        const conv = this.conversations.find((c) => c.id === conversationId);
        if (conv) {
          conv.unread_count = 0;
          conv.marked_unread = false;
        }
        const hiddenConv = this.hiddenConversations.find((c) => c.id === conversationId);
        if (hiddenConv && (hiddenConv.unread_count > 0 || hiddenConv.marked_unread)) {
          hiddenConv.unread_count = 0;
          hiddenConv.marked_unread = false;
        }
      } catch (err) {
        console.error('Failed to load messages:', err);
      } finally {
        if (this.activeConversation) this.activeConversation.isLoading = false;
      }
    },

    mergeMessagesById(first, second) {
      const seen = new Set();
      const merged = [];
      [...first, ...second].forEach((message) => {
        if (seen.has(message.id)) return;
        seen.add(message.id);
        merged.push(message);
      });
      return merged;
    },

    clearMessageHighlight() {
      if (this._highlightTimer) {
        clearTimeout(this._highlightTimer);
        this._highlightTimer = null;
      }
      this.highlightedMessageId = null;
    },

    highlightAndScrollToMessage(messageId) {
      this.clearMessageHighlight();
      this.highlightedMessageId = messageId;

      this.$nextTick(() => {
        const container = this.$refs.messageContainer;
        const el = container?.querySelector(`[data-message-id="${messageId}"]`);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });

      // Keep the yellow band visible for ~9s so the user can find the message
      this._highlightTimer = setTimeout(() => {
        this.highlightedMessageId = null;
        this._highlightTimer = null;
      }, 9000);
    },

    // The action bar sits above received messages in the popup; when there is
    // no room above (message at the top of the scroll area) it would be
    // clipped, so it drops below the message instead.
    placeMessageActions(messageEl) {
      if (messageEl.closest('.chat-fullpage')) return;

      const container = this.$refs.messageContainer;
      const bar = messageEl.querySelector('.chat-overlay__message-actions');
      if (!container || !bar) return;

      const roomAbove = messageEl.getBoundingClientRect().top - container.getBoundingClientRect().top;
      messageEl.classList.toggle('chat-overlay__message--actions-below', roomAbove < bar.offsetHeight);
    },

    // ─────────────────────────────────────────
    // Real-time Message Handling
    // ─────────────────────────────────────────
    resolvePreviewContent(content, mentions) {
      if (!content || !mentions?.length) return content;
      return content.replace(/@\{\{([\w-]+)\}\}/g, (_, uuid) => {
        const mention = mentions.find((m) => m.attributes.uuid === uuid);
        return mention ? `@${mention.attributes.name}` : `@${uuid}`;
      });
    },

    handleNewMessage(conversationId, message) {
      const convIndex = this.conversations.findIndex((c) => c.id === conversationId);
      if (convIndex !== -1) {
        const conv = this.conversations[convIndex];
        conv.last_message = {
          id: message.id,
          content: this.resolvePreviewContent(this.displayableContent(message), message.mentions),
          user_id: message.user_id,
          sent_at: message.sent_at,
          attachment_type: message.attachment_type
        };
        conv.last_message_at = message.sent_at;

        if (this.activeConversationId !== conversationId && message.user_id !== currentUserId) {
          conv.unread_count = (conv.unread_count || 0) + 1;
        }

        this.conversations.splice(convIndex, 1);
        this.conversations.unshift(conv);
      }

      if (this.activeConversationId !== conversationId) return;

      // Skip if this message already exists (prevents duplicates from race conditions)
      if (this.activeConversation.messages.some((m) => m.id === message.id)) {
        return;
      }

      // Check if this is confirming a pending message we sent
      const pendingIndex = this.activeConversation.messages.findIndex(
        (m) => m.pending && m.id === message.pending_id
      );

      if (pendingIndex !== -1) {
        // Replace pending message with confirmed one
        this.activeConversation.messages.splice(pendingIndex, 1, message);
      } else if (this.activeConversation.hasNewer) {
        // Mid-history window — don't append a discontinuous newest message
        return;
      } else {
        // New message from someone else
        this.activeConversation.messages.push(message);
        this.scrollToBottom();

        // Mark as read if the message is from someone else
        if (message.user_id !== currentUserId) {
          this.markConversationAsRead(conversationId);
        }
      }
    },

    handleMessageHidden(conversationId, messageId, hidden) {
      if (this.activeConversationId !== conversationId) return;

      const message = this.findActiveMessage(messageId);
      if (message) {
        message.hidden = hidden;
      }
    },

    handleMessageDeleted(conversationId, messageId) {
      // Refresh the conversation list preview if the deleted message was the latest.
      const conv = this.conversations.find((c) => c.id === conversationId);
      if (conv?.last_message?.id === messageId) {
        const key = conv.last_message.user_id === currentUserId
          ? 'chat.formatters.deleted_message_own'
          : 'chat.formatters.deleted_message_other';
        conv.last_message = {
          ...conv.last_message,
          content: window.i18n?.t(key) || key,
          attachment_type: null,
          deleted: true
        };
      }

      if (this.activeConversationId !== conversationId) return;

      const message = this.activeConversation.messages.find((m) => m.id === messageId);
      if (message) {
        message.deleted = true;
      }

      // Swap any loaded replies that quote the deleted message to the placeholder,
      // so their reply preview updates live instead of after a refresh.
      this.activeConversation.messages.forEach((m) => {
        if (m.reply_to?.id !== messageId) return;

        const key = m.reply_to.user?.id === currentUserId
          ? 'chat.formatters.deleted_message_own'
          : 'chat.formatters.deleted_message_other';
        m.reply_to = {
          ...m.reply_to,
          content: window.i18n?.t(key) || key,
          deleted: true,
          mentions: []
        };
      });
    },

    // Shared by both channels (conversation + inbox) and by the editor's own
    // API response. Merges only the content-bearing fields instead of replacing
    // the message wholesale: broadcast payloads are serialized from the editor's
    // perspective, so viewer-specific state (reactions, is_favorite) must be
    // left untouched for everyone else.
    handleMessageEdited(conversationId, message) {
      // Conversation-list preview, if the edited message is the latest.
      const conv = this.conversations.find((c) => c.id === conversationId);
      if (conv?.last_message?.id === message.id) {
        conv.last_message = {
          ...conv.last_message,
          content: this.resolvePreviewContent(this.displayableContent(message), message.mentions)
        };
      }

      if (this.activeConversationId !== conversationId) return;

      const contentFields = {
        content: message.content,
        content_fr: message.content_fr,
        original_lang: message.original_lang,
        mentions: message.mentions
      };

      const existing = this.findActiveMessage(message.id);
      if (existing) {
        Object.assign(existing, contentFields, {
          edited_at: message.edited_at,
          // The edit cleared the stale machine translation server-side, so any
          // in-flight "translating" state no longer applies.
          translating: false
        });
      }

      // Replies quoting the edited message update their preview live.
      this.activeConversation.messages.forEach((m) => {
        if (m.reply_to?.id !== message.id) return;
        m.reply_to = { ...m.reply_to, ...contentFields };
      });
    },

    handleReadReceipt(conversationId, userId, lastReadAt) {
      // Ignore our own read receipts
      if (userId === currentUserId) return;

      if (this.activeConversationId !== conversationId) return;

      // Track the furthest point any other member has read to. This is
      // conversation-level state (not a per-message flag), so it survives a
      // message being re-serialized when its ActionCable confirmation lands —
      // which is what makes "Delete for everyone" hide reliably and live.
      // Keep the max so a slower reader in a group can't move it backwards.
      const current = this.activeConversation.otherParticipantLastReadAt;
      if (!current || new Date(lastReadAt) > new Date(current)) {
        this.activeConversation.otherParticipantLastReadAt = lastReadAt;
      }
    },

    async markConversationAsRead(conversationId) {
      try {
        const response = await this.$api({
          url: `/chat/conversations/${conversationId}/memberships/read`,
          method: 'POST'
        });
        this.applyReadLocally([conversationId]);
        this.loadHiddenUnreadCount();
        this.syncNotificationBell(response, [conversationId]);
      } catch (err) {
        console.error('Failed to mark conversation as read:', err);
      }
    },

    // Reading a conversation clears its notification activities server-side. The
    // bell is a separate component that never learns this on its own, so hand it
    // the authoritative count and tell it which rows no longer apply.
    syncNotificationBell(response, conversationIds) {
      const count = response?.data?.unread_notification_count;
      if (typeof count === 'number') {
        this.$dispatch('set-unreadcount', count);
      }
      conversationIds.forEach((id) => this.$dispatch('clear-conversation-notifications', id));
    },

    // The activity behind a chat notification is written by a queued job, so
    // this arrives once that row actually exists — the dropdown can always find
    // what the count promised, instead of racing to open it and finding nothing.
    handleUnreadNotificationCount(count) {
      this.$dispatch('receive-unreadcount', count);
    },

    handleInboxMessage(conversationId, message) {
      // Skip if this conversation is currently active (ConversationChannel handles it)
      if (this.activeConversationId === conversationId) {
        return;
      }

      // Blocked DMs stay off the inbox — if we still have the row cached, update
      // its preview in place rather than promoting it back into the main list.
      const blockedIndex = this.blockedConversations.findIndex((c) => c.id === conversationId);
      if (blockedIndex !== -1) {
        const conv = this.blockedConversations[blockedIndex];
        conv.last_message = {
          id: message.id,
          content: this.resolvePreviewContent(this.displayableContent(message), message.mentions),
          user_id: message.user_id,
          sent_at: message.sent_at,
          attachment_type: message.attachment_type
        };
        conv.last_message_at = message.sent_at;
        return;
      }

      const convIndex = this.conversations.findIndex((c) => c.id === conversationId);
      if (convIndex !== -1) {
        const conv = this.conversations[convIndex];
        conv.last_message = {
          id: message.id,
          content: this.resolvePreviewContent(this.displayableContent(message), message.mentions),
          user_id: message.user_id,
          sent_at: message.sent_at,
          attachment_type: message.attachment_type
        };
        conv.last_message_at = message.sent_at;
        conv.unread_count = (conv.unread_count || 0) + 1;

        // Move to top of list
        this.conversations.splice(convIndex, 1);
        this.conversations.unshift(conv);
      } else {
        // New conversation we don't know about yet - reload the list
        this.loadConversations();
      }

      this.loadHiddenUnreadCount();
    },

    // ─────────────────────────────────────────
    // Sending Messages
    // ─────────────────────────────────────────
    async sendMessage(conversationId, content, replyTo = null, mentions = []) {
      if (!content.trim()) return null;

      if (this.isBlockedByOther()) {
        this.appendUndeliverableNotice();
        return null;
      }

      const active =
        this.activeConversationId === conversationId ? this.activeConversation : null;

      // Jump to the newest page before sending so the optimistic message isn't
      // appended onto a mid-history window with a gap.
      if (active?.hasNewer) {
        await this.loadMessages(conversationId);
      }

      // Create optimistic message (no sent_at = no checkmark)
      const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const pendingMessage = {
        id: pendingId,
        conversation_id: conversationId,
        user_id: currentUserId,
        user: { id: currentUserId },
        content: content.trim(),
        mentions,
        message_type: 'text',
        sent_at: null,
        created_at: new Date().toISOString(),
        pending: true,
        reply_to: replyTo || null
      };

      // Add to UI immediately
      if (active) {
        active.messages.push(pendingMessage);
        this.scrollToBottom();
      }

      try {
        const response = await this.$api({
          url: `/chat/conversations/${conversationId}/messages`,
          method: 'POST',
          data: { content, message_type: 'text', ...(replyTo?.id && { reply_to_id: replyTo.id }), pending_id: pendingId }
        });
        this.sendError = null;
        this.trackMessageSend('text', { replyTo, mentions });
        // Don't update UI here - let ActionCable handle replacing the pending message
        // This avoids race conditions between API response and broadcast
        return response.data.message;
      } catch (err) {
        console.error('Failed to send message:', err);

        // Remove the pending message on failure
        if (active) {
          const pendingIndex = active.messages.findIndex((m) => m.id === pendingMessage.id);
          if (pendingIndex !== -1) {
            active.messages.splice(pendingIndex, 1);
          }
        }

        await this.handleSendError(err);
        return null;
      }
    },

    async sendAttachment({
      conversationId,
      file,
      content = '',
      altText = '',
      altTextFr = '',
      waveform = null,
      duration = null,
      replyTo = null,
      mentions = []
    }) {
      if (this.isBlockedByOther()) {
        this.appendUndeliverableNotice();
        return null;
      }

      const active =
        this.activeConversationId === conversationId ? this.activeConversation : null;

      if (active?.hasNewer) {
        await this.loadMessages(conversationId);
      }

      // Create optimistic message with local preview URL
      const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const fileCategory = file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('video/')
          ? 'video'
          : file.type.startsWith('audio/')
            ? 'audio'
            : 'file';

      const pendingMessage = {
        id: pendingId,
        conversation_id: conversationId,
        user_id: currentUserId,
        user: { id: currentUserId },
        content: content.trim(),
        mentions,
        message_type: 'media',
        attachment_type: fileCategory,
        attachment_name: file.name,
        attachment_size: file.size,
        attachment_preview_url: fileCategory === 'image' ? URL.createObjectURL(file) : null,
        attachment_alt_text: fileCategory === 'image' ? altText || null : null,
        attachment_alt_text_fr: fileCategory === 'image' ? altTextFr || null : null,
        attachment_waveform: fileCategory === 'audio' ? waveform : null,
        attachment_duration: fileCategory === 'audio' ? duration : null,
        sent_at: null,
        created_at: new Date().toISOString(),
        pending: true,
        uploading: true,
        reply_to: replyTo || null
      };

      if (active) {
        active.messages.push(pendingMessage);
        this.scrollToBottom();
      }

      const formData = new FormData();
      formData.append('attachment', file);
      formData.append('content', content);
      formData.append('message_type', 'media');
      if (replyTo?.id) formData.append('reply_to_id', replyTo.id);
      if (fileCategory === 'image' && altText) formData.append('attachment_alt_text', altText);
      if (fileCategory === 'image' && altTextFr) formData.append('attachment_alt_text_fr', altTextFr);
      if (fileCategory === 'audio') {
        // Joined rather than repeated: 48 separate multipart fields would spend
        // ~5KB of boundary and header framing to carry ~140 bytes of peaks.
        if (waveform?.length) formData.append('attachment_waveform', waveform.join(','));
        if (duration) formData.append('attachment_duration', duration);
      }
      formData.append('pending_id', pendingId);

      try {
        const response = await this.$api({
          url: `/chat/conversations/${conversationId}/messages`,
          method: 'POST',
          data: formData
        });
        this.sendError = null;
        this.trackMessageSend(fileCategory === 'audio' ? 'voice' : fileCategory, { replyTo, mentions });

        // Clean up the local preview URL to prevent memory leaks
        if (pendingMessage.attachment_preview_url) {
          URL.revokeObjectURL(pendingMessage.attachment_preview_url);
        }

        // Don't update UI here - let ActionCable handle replacing the pending message
        return response.data.message;
      } catch (err) {
        console.error('Failed to send attachment:', err);

        if (active) {
          const pendingIndex = active.messages.findIndex((m) => m.id === pendingMessage.id);
          if (pendingIndex !== -1) {
            if (pendingMessage.attachment_preview_url) {
              URL.revokeObjectURL(pendingMessage.attachment_preview_url);
            }
            active.messages.splice(pendingIndex, 1);
          }
        }

        await this.handleSendError(err);
        return null;
      }
    },

    async handleSendError(err) {
      if (err?.status === 403 && !this.isGroupConversation()) {
        const conv = this.getConversation(this.activeConversationId);
        if (conv) conv.is_blocked = true;
        this.appendUndeliverableNotice();
        return;
      }

      let errorMessage = 'Failed to send message. Please try again.';
      try {
        // err is a Response object, need to parse JSON body
        const errorData = await err.json();
        if (errorData?.errors) {
          errorMessage = errorData.errors.join(', ');
        }
      } catch {
        // Couldn't parse JSON, use default message
      }

      this.sendError = errorMessage;

      // Clear error after 5 seconds
      setTimeout(() => {
        this.sendError = null;
      }, 5000);
    },

    appendUndeliverableNotice() {
      const active = this.activeConversation;
      if (!active) return;

      const last = active.messages[active.messages.length - 1];
      if (last?.local_notice === 'undeliverable') {
        this.scrollToBottom();
        return;
      }

      active.messages.push({
        id: `undeliverable-${Date.now()}`,
        local_notice: 'undeliverable',
        created_at: new Date().toISOString()
      });
      this.scrollToBottom();
    },

    // ─────────────────────────────────────────
    // Favorite a Message
    // ─────────────────────────────────────────
    async handleFavoriteMessage(id) {
      if (this.areMessageActionsHidden()) return;

      try {
        await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/messages/${id}/favorite`,
          method: 'POST',
          data: { chat_message_id: id }
        });

        const message = this.findActiveMessage(id);
        if (message) {
          message.is_favorite = true;
        }
        this.trackChat('favorites', 'add');
        this.addMessageToFavoriteConversations(message);

      } catch (err) {
        console.error('Failed to favorite message:', err);
        if (err instanceof Response) {
          try {
            const data = await err.json();
            this.favoriteMessageError = data.errors?.[0] || 'Failed to favorite message';
          } catch {
            this.favoriteMessageError = 'Failed to favorite message';
          }
        } else {
          this.favoriteMessageError = 'Failed to favorite message';
        }
      }
    },

    async handleTranslateMessage(message) {
      const targetField = message.original_lang === 'fr' ? 'content' : 'content_fr';
      if (message[targetField] || message.translating) return;

      message.translating = true;

      try {
        const response = await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/messages/${message.id}/translate`,
          method: 'POST'
        });
        this.trackChat('translate', 'click');

        const updated = response.data.message;
        const index = this.activeConversation.messages.findIndex((m) => m.id === message.id);
        if (index !== -1) {
          this.activeConversation.messages.splice(index, 1, updated);
        }
      } catch (err) {
        console.error('Failed to translate message:', err);
        message.translating = false;
        const translationFailed = window.i18n?.t('chat.translation_failed');
        if (err instanceof Response) {
          try {
            const data = await err.json();
            this.sendError = data.error || translationFailed;
          } catch {
            this.sendError = translationFailed;
          }
        } else {
          this.sendError = translationFailed;
        }
      }
    },

    async handleRemoveFavoriteMessage(id) {
      if (this.areMessageActionsHidden()) return;

      try {
        await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/messages/${id}/favorite`,
          method: 'DELETE',
        });

        const message = this.findActiveMessage(id);
        if (message) {
          message.is_favorite = false;
        }
        this.removeMessageFromFavoriteConversations(id);

      } catch (err) {
        console.error('Failed to remove favorite message:', err);
        if (err instanceof Response) {
          try {
            const data = await err.json();
            this.favoriteMessageError = data.errors?.[0] || 'Failed to remove favorite message';
          } catch {
            this.favoriteMessageError = 'Failed to remove favorite message';
          }
        } else {
          this.favoriteMessageError = 'Failed to remove favorite message';
        }
      }
    },

    // ─────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────
    findActiveMessage(id) {
      return this.activeConversation?.messages.find((m) => m.id === id);
    },

    // ─────────────────────────────────────────
    // Emoji Reactions
    // ─────────────────────────────────────────
    quickReactions: ['👍', '❤️', '😊', '😂', '😮', '😢'],
    reactionPickerOpen: false,
    reactionPickerMessageId: null,
    reactionPickerStyle: '',
    _reactionPickerEl: null,
    _reactionPickerPromise: null,

    // POST to add / DELETE to remove, chosen from the viewer's own reacted state.
    // The UI updates optimistically and reconciles against the server's full
    // per-viewer summary; the broadcast keeps other clients (and our other
    // devices) in sync.
    async toggleReaction(messageId, emoji) {
      if (this.areMessageActionsHidden()) return;

      const message = this.findActiveMessage(messageId);
      if (!message) return;

      const existing = (message.reactions || []).find((r) => r.emoji === emoji);
      const adding = !existing?.reacted_by_me;

      this.applyReactionDelta(message, emoji, adding);

      try {
        const response = await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/messages/${messageId}/reaction`,
          method: adding ? 'POST' : 'DELETE',
          query: { emoji }
        });
        message.reactions = response.data.reactions;
        if (adding) this.trackChat('reactions', 'add');
      } catch (err) {
        // Roll back the optimistic change; the server rejected it (rate limit,
        // distinct-emoji cap, etc.).
        this.applyReactionDelta(message, emoji, !adding);
        console.error('Failed to toggle reaction:', err);
      }
    },

    // Optimistic, self-only mutation: bumps/removes the viewer's own contribution
    // to an emoji's count. Server responses and broadcasts overwrite this.
    applyReactionDelta(message, emoji, adding) {
      message.reactions = message.reactions || [];
      const entry = message.reactions.find((r) => r.emoji === emoji);

      if (adding) {
        if (entry) {
          if (!entry.reacted_by_me) {
            entry.count += 1;
            entry.reacted_by_me = true;
          }
        } else {
          message.reactions.push({ emoji, count: 1, reacted_by_me: true });
        }
      } else if (entry?.reacted_by_me) {
        entry.count -= 1;
        entry.reacted_by_me = false;
        if (entry.count <= 0) {
          message.reactions = message.reactions.filter((r) => r.emoji !== emoji);
        }
      }
    },

    // Broadcast carries an absolute per-emoji count (not a delta), so applying it
    // is idempotent against the echo of our own action.
    handleReaction(conversationId, data) {
      if (this.activeConversationId !== conversationId) return;

      const message = this.findActiveMessage(data.message_id);
      if (!message) return;

      message.reactions = message.reactions || [];
      const entry = message.reactions.find((r) => r.emoji === data.emoji);

      if (data.count <= 0) {
        message.reactions = message.reactions.filter((r) => r.emoji !== data.emoji);
      } else if (entry) {
        entry.count = data.count;
      } else {
        message.reactions.push({ emoji: data.emoji, count: data.count, reacted_by_me: false });
      }

      // Only the actor's own clients flip their highlight — that's why actor_id
      // and reacted ride along on the broadcast.
      if (data.actor_id === currentUserId) {
        const target = message.reactions.find((r) => r.emoji === data.emoji);
        if (target) target.reacted_by_me = data.reacted;
      }
    },

    async openReactionPicker(messageId, event) {
      this.reactionPickerMessageId = messageId;

      // Anchor the picker above the clicked control, clamped to the viewport.
      const rect = event.currentTarget.getBoundingClientRect();
      const PICKER = { width: 352, height: 360 };
      const top = Math.max(8, rect.top - PICKER.height - 8);
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - PICKER.width - 8);
      this.reactionPickerStyle = `top: ${top}px; left: ${left}px;`;

      this.reactionPickerOpen = true;

      // Cache the in-flight build so re-opens during the (lazy, slow) first
      // import share one picker instead of each appending its own.
      if (!this._reactionPickerEl && !this._reactionPickerPromise) {
        this._reactionPickerPromise = createEmojiPicker({
          onEmojiSelect: (emoji) => {
            this.toggleReaction(this.reactionPickerMessageId, emoji.native);
            this.closeReactionPicker();
          }
        }).then((el) => {
          // Shared singleton container at the chatOverlay root.
          document.getElementById('chat-reaction-picker').appendChild(el);
          this._reactionPickerEl = el;
        }).catch((err) => {
          this._reactionPickerPromise = null;
          throw err;
        });
      }

      await this._reactionPickerPromise;
    },

    closeReactionPicker() {
      this.reactionPickerOpen = false;
      this.reactionPickerMessageId = null;
    },
  };
}
